import type { ContentTypeDefinition } from '../../../lib/flotiq-client/types/api-response';
import type { DataGridColumnProps } from '../../DataGrid/DataGrid';
import type { DropdownChangeFn } from '../../Dropdown/Dropdown';
import type {
  FilterValue,
  GridFilters,
  GridFiltersChangeFn,
  GridSingleFilter,
} from '../filterDefinitions';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { GridRenderFilterEvent } from '../../../lib/flotiq-plugins/plugin-events/GridRenderFilterEvent';
import { getTestProps } from '../../../lib/helpers';

import usePluginResults from '../../../hooks/usePluginResults';

import { CloseIcon } from '../../../images/shapes';

import Dropdown from '../../Dropdown/Dropdown';
import ElementFromPlugin from '../../ElementFromPlugin/ElementFromPlugin';
import {
  FilterOperator,
  getDefaultFilterOperator,
  getFilterOperators,
} from '../filterDefinitions';
import GridFilterValueField from './GridFilterValueField';

interface GridFilterProps {
  /**
   * The name of the field being filtered.
   */
  fieldName: string;
  /**
   * The filter object containing the operator type and the filter value.
   */
  filter: GridSingleFilter;
  /**
   * The current filters applied to the grid.
   */
  filters: GridFilters;
  /**
   * Callback fired when the filters are changed.
   */
  onFiltersChange: GridFiltersChangeFn;
  /**
   * The column definition for the field being filtered.
   */
  column?: DataGridColumnProps;
  /**
   * Whether to show the operator selector.
   */
  showOperators?: boolean;
  /**
   * The content type of the items being filtered.
   */
  contentType?: ContentTypeDefinition | null;
  /**
   * Test ID for the filter.
   */
  testId?: string;
}

const EMPTY_OPERATORS = [FilterOperator.Empty, FilterOperator.NotEmpty];

const GridFilter = ({
  fieldName,
  filter,
  filters,
  onFiltersChange,
  column,
  showOperators = true,
  contentType,
  testId,
}: GridFilterProps) => {
  const { t } = useTranslation();
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);

  useEffect(() => {
    // Keep these refs current so the debounced value update uses the latest filters and callback.
    filtersRef.current = filters;
    onFiltersChangeRef.current = onFiltersChange;
  }, [filters, onFiltersChange]);

  const operatorOptions = getFilterOperators(column?.filterInputType).map(
    (operator) => ({
      label: t(`ObjectsOfType.FilterOperators.${operator}`),
      value: operator,
    }),
  );

  const onOperatorChange: DropdownChangeFn = (e) => {
    const value = e.target.value;
    const newFilters = { ...filters };

    newFilters[fieldName] = { ...newFilters?.[fieldName], type: value };

    if (EMPTY_OPERATORS.includes(value)) {
      delete newFilters[fieldName].filter;
      delete newFilters[fieldName].filter2;
    } else if (value === FilterOperator.InRange) {
      newFilters[fieldName].filter ??= '';
      newFilters[fieldName].filter2 ??= '';
    } else {
      delete newFilters[fieldName].filter2;
      newFilters[fieldName].filter ??= '';
    }

    onFiltersChange(newFilters);
  };

  const onValueChange = (value: FilterValue, key: 'filter' | 'filter2') => {
    const newFilters = { ...filtersRef.current };

    if (!newFilters[fieldName]) {
      newFilters[fieldName] = {
        type: getDefaultFilterOperator(column?.filterInputType),
        [key]: value,
      };
    } else {
      newFilters[fieldName] = {
        ...newFilters[fieldName],
        [key]: value,
      };
    }

    onFiltersChangeRef.current(newFilters);
  };

  const handleFilterRemove = () => {
    const newFilters = { ...filters };
    delete newFilters[fieldName];
    onFiltersChange(newFilters);
  };

  /**
   * @emits FlotiqPlugins."flotiq.grid.filter::render"
   */
  const results = usePluginResults(
    'flotiq.grid.filter::render',
    GridRenderFilterEvent,
    {
      inputType: column?.filterInputType,
      accessor: column?.accessor,
      updateFilters: onFiltersChange,
      allFilters: filters,
      contentType,
    },
  );

  if (results && results.length > 0) {
    return <ElementFromPlugin results={results} />;
  }

  return (
    <div
      className="flex flex-wrap min-w-0 max-w-full items-center gap-2 bg-white dark:bg-slate-800
                min-h-fit h-8 pl-3 pr-1.5 py-0.5 rounded-lg border-1 border-slate-200 dark:border-slate-700 
                text-slate-700 dark:text-slate-200"
    >
      <span className="min-w-0 max-w-full truncate">
        {column?.label || fieldName}
      </span>

      {showOperators && (
        <Dropdown
          value={filter.type}
          options={operatorOptions}
          onChange={onOperatorChange}
          variant="operator"
          additionalDropdownClasses="min-w-max"
          hideSearch
          {...getTestProps(testId, 'operator', 'testId')}
        />
      )}

      {(!showOperators ||
        !EMPTY_OPERATORS.includes(filter.type as FilterOperator)) && (
        <>
          <GridFilterValueField
            column={column}
            onChange={(value) => onValueChange(value, 'filter')}
            value={filter.filter}
            {...getTestProps(testId, 'value', 'testId')}
          />
          {filter.type === FilterOperator.InRange && (
            <GridFilterValueField
              column={column}
              onChange={(value) => onValueChange(value, 'filter2')}
              value={filter.filter2}
              {...getTestProps(testId, 'value2', 'testId')}
            />
          )}
        </>
      )}

      <div className="ml-auto">
        <button
          className={twMerge(
            'ml-1 md:ml-3 flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded cursor-pointer',
            'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700',
          )}
          type="button"
          onClick={handleFilterRemove}
          aria-label={t('ObjectsOfType.RemoveFilter')}
          {...getTestProps(testId, 'remove')}
        >
          <CloseIcon className="h-2 w-2" />
        </button>
      </div>
    </div>
  );
};

export default GridFilter;
